/**
 * Angebot (Kapitel L).
 *
 * Der Knopf ist absichtlich streng: Ein Angebot wird erst nach erfolgtem
 * Abgleich erzeugt (PV-B16). Nummernvergabe und Versand gehoeren auf die
 * Serverseite — offline geplant wird, offline angeboten nicht.
 *
 * Beim Anlegen wird der Projektstand eingefroren (PV-C03). Was danach am
 * Projekt geaendert wird, aendert das Angebot nicht mehr; sonst stimmt das
 * versendete PDF nicht mehr mit den Daten ueberein.
 */

import { useState } from 'react';

import { anmeldung } from '../daten/abgleich.ts';
import { db, neueId } from '../daten/db.ts';
import { hakeAbFallsOffen } from '../daten/schritte.ts';
import { tagAnzeige } from '../datum.ts';
import type { Anlagendaten } from '../anlage.ts';
import type { Ertragsdaten } from '../ertragrechnung.ts';
import type {
  Angebotsstatus,
  Angebotszeile,
  Belegung,
  Flaeche,
  Projekt,
  Projektkomponente,
  Stringzeile,
} from '../typen.ts';

interface Eigenschaften {
  projekt: Projekt;
  flaechen: Flaeche[];
  belegungen: Belegung[];
  strings: Stringzeile[];
  projektkomponenten: Projektkomponente[];
  anlage: Anlagendaten;
  ertrag: Ertragsdaten;
  angebote: Angebotszeile[];
  /** Offene Aenderungen — solange welche da sind, kein Angebot (PV-B16). */
  offeneAenderungen: number;
  online: boolean;
  onDrucken: (angebot: Angebotszeile) => void;
}

const STATUSTEXT: Record<Angebotsstatus, string> = {
  entwurf: 'Entwurf',
  versendet: 'Versendet',
  angenommen: 'Angenommen',
  abgelehnt: 'Abgelehnt',
};

export function Angebotspanel(e: Eigenschaften): React.JSX.Element {
  const [laeuft, setLaeuft] = useState(false);
  const [meldung, setMeldung] = useState<string | null>(null);
  const [variante, setVariante] = useState('Standard');

  // Ein Dach ohne gewählte Traufe rechnet mit Kante 1 als Traufe — eine
  // Zufallsrichtung, und der Ertrag im Angebot hinge daran.
  const ohneTraufe = e.flaechen.filter(
    (f) => f.art === 'dach' && f.traufkante_index === null && f.azimut_ueberschrieben === null && f.neigung_grad > 0,
  );
  const bereit =
    ohneTraufe.length === 0 &&
    e.online &&
    e.offeneAenderungen === 0 &&
    e.anlage.material.pauschal.length > 0 &&
    e.ertrag.leistungKwp > 0;

  async function ruf(pfad: string, optionen: RequestInit): Promise<Response> {
    const an = await anmeldung();
    const kopf = new Headers(optionen.headers);
    kopf.set('Content-Type', 'application/json');
    if (an) kopf.set('Authorization', `Bearer ${an.token}`);
    return fetch(pfad, { ...optionen, headers: kopf });
  }

  /** Den Projektstand einfrieren und beim Server ein Angebot anlegen. */
  async function legeAn(): Promise<void> {
    setLaeuft(true);
    setMeldung(null);
    try {
      const stand = {
        projekt: e.projekt as unknown as Record<string, unknown>,
        flaechen: e.flaechen,
        belegungen: e.belegungen,
        strings: e.strings,
        komponenten: e.projektkomponenten,
        leitungswege: [],
        material: {
          pauschal: e.anlage.material.pauschal,
          detailliert: e.anlage.material.detailliert,
          summeEinkauf: e.anlage.material.summeEinkauf,
          summeVerkauf: e.anlage.material.summeVerkauf,
        },
        wirtschaftlichkeit: e.ertrag.wirtschaftlichkeit
          ? {
              investitionEuro: e.ertrag.wirtschaftlichkeit.investitionEuro,
              amortisationJahre: e.ertrag.wirtschaftlichkeit.amortisationJahre,
              ersparnisErstesJahrEuro: e.ertrag.wirtschaftlichkeit.ersparnisErstesJahrEuro,
              einspeiseerloesErstesJahrEuro:
                e.ertrag.wirtschaftlichkeit.einspeiseerloesErstesJahrEuro,
              bilanz: e.ertrag.wirtschaftlichkeit.bilanz,
              rechenweg: e.ertrag.wirtschaftlichkeit.rechenweg,
              ertragJahr: e.ertrag.ertrag.jahr,
              ertragMonate: e.ertrag.ertrag.monate,
              leistungKwp: e.ertrag.leistungKwp,
            }
          : null,
        stammdatenstand: {
          erstelltAm: new Date().toISOString(),
          strahlungsquelle: e.ertrag.istNaeherung ? 'Rückfallwerte' : 'PVGIS',
        },
      };

      const antwort = await ruf('/api/angebote', {
        method: 'POST',
        body: JSON.stringify({
          id: neueId(),
          projektId: e.projekt.id,
          variante,
          summeNetto: e.anlage.material.summeVerkauf,
          stand,
        }),
      });
      if (!antwort.ok) throw new Error(`Server antwortet mit ${antwort.status}`);

      const { angebot } = (await antwort.json()) as { angebot: Angebotszeile };
      await db.angebote.put({ ...angebot, seq: null });
      setMeldung(`Angebot ${angebot.nummer} angelegt.`);
    } catch (fehler) {
      setMeldung(`Angebot konnte nicht angelegt werden: ${String(fehler)}`);
    } finally {
      setLaeuft(false);
    }
  }

  /**
   * Ein Link mit `download` schickt keine Anmeldung mit — der Export kam immer
   * als 401 heraus, und in der Datei stand die Fehlermeldung. Deshalb geholt
   * und als Datei übergeben.
   */
  async function ladeBuchhaltung(angebot: Angebotszeile): Promise<void> {
    try {
      const antwort = await ruf(`/api/angebote/${angebot.id}/buchhaltung.csv`, { method: 'GET' });
      if (!antwort.ok) throw new Error(`Server antwortet mit ${antwort.status}`);
      const url = URL.createObjectURL(await antwort.blob());
      const link = document.createElement('a');
      link.href = url;
      link.download = `${angebot.nummer}.csv`;
      document.body.append(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (fehler) {
      setMeldung(`Buchhaltungsexport nicht geladen: ${String(fehler)}`);
    }
  }

  async function setzeStatus(angebot: Angebotszeile, status: Angebotsstatus): Promise<void> {
    try {
      const antwort = await ruf(`/api/angebote/${angebot.id}/status`, {
        method: 'POST',
        body: JSON.stringify({ status }),
      });
      if (!antwort.ok) throw new Error(`Server antwortet mit ${antwort.status}`);
      const { angebot: neu } = (await antwort.json()) as { angebot: Angebotszeile };
      await db.angebote.put({ ...neu, seq: angebot.seq });
      // Der Ablauf hält fest, wann und von wem — für die Übersicht.
      if (status === 'versendet' || status === 'angenommen') await hakeAbFallsOffen(e.projekt.id, 'angebot');
      if (status === 'angenommen') await hakeAbFallsOffen(e.projekt.id, 'auftrag');
    } catch (fehler) {
      setMeldung(`Status konnte nicht gesetzt werden: ${String(fehler)}`);
    }
  }

  return (
    <div>
      <h2>Angebot</h2>

      {!e.online && (
        <div className="hinweis">
          Ohne Verbindung kein Angebot: Die Nummer vergibt der Server (PV-B16). Planen geht
          weiterhin.
        </div>
      )}
      {e.offeneAenderungen > 0 && (
        <div className="hinweis">
          Erst abgleichen — {e.offeneAenderungen} Änderung(en) sind noch nicht übertragen. Sonst
          friert das Angebot einen Stand ein, den der Server nicht kennt.
        </div>
      )}
      {e.ertrag.leistungKwp === 0 && <div className="hinweis">Noch keine Module belegt.</div>}
      {ohneTraufe.length > 0 && (
        <div className="hinweis hinweis--warnung">
          Bei {ohneTraufe.map((f) => `„${f.name}“`).join(', ')} ist noch keine Traufkante gewählt. Ohne
          sie stimmt die Ausrichtung nicht, und der Ertrag im Angebot wäre geraten.
        </div>
      )}
      {!e.ertrag.wirtschaftlichkeit && e.ertrag.leistungKwp > 0 && (
        <div className="hinweis">
          Das Angebot lässt sich anlegen, enthält aber keine Wirtschaftlichkeitsrechnung —
          Strompreis und Einspeisevergütung fehlen.
        </div>
      )}

      <div className="feld">
        <label htmlFor="variante">Variante</label>
        <input
          id="variante"
          value={variante}
          onChange={(ereignis) => setVariante(ereignis.target.value)}
          placeholder="z. B. mit Speicher"
        />
        <p style={{ fontSize: '0.78rem', color: 'var(--grau)', margin: '0.3rem 0 0' }}>
          Mehrere Varianten desselben Projekts sind möglich — etwa mit und ohne Speicher.
        </p>
      </div>

      <div className="knopfreihe">
        <button className="wichtig" onClick={() => void legeAn()} disabled={!bereit || laeuft}>
          {laeuft ? 'Legt an …' : 'Angebot erzeugen'}
        </button>
      </div>
      {meldung && <div className="hinweis">{meldung}</div>}

      {e.angebote.length === 0 ? (
        <p style={{ color: 'var(--grau)', fontSize: '0.9rem', marginTop: '1rem' }}>
          Noch kein Angebot zu diesem Projekt.
        </p>
      ) : (
        e.angebote.map((angebot) => (
          <div
            key={angebot.id}
            style={{ borderTop: '1px solid var(--linie)', padding: '0.7rem 0' }}
          >
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'baseline' }}>
              <strong style={{ flex: 1 }}>{angebot.nummer}</strong>
              <span className="marke">{STATUSTEXT[angebot.status]}</span>
            </div>
            <div className="klein">
              {angebot.variante} · {tagAnzeige(angebot.datum)}
              {angebot.summe_netto != null &&
                ` · ${Number(angebot.summe_netto).toLocaleString('de-AT', {
                  style: 'currency',
                  currency: 'EUR',
                  maximumFractionDigits: 0,
                })}`}
              {angebot.gueltig_bis && ` · gültig bis ${tagAnzeige(angebot.gueltig_bis)}`}
            </div>

            <div className="knopfreihe" style={{ marginTop: '0.4rem' }}>
              <button onClick={() => e.onDrucken(angebot)}>Ansehen und drucken</button>
              {angebot.status === 'entwurf' && (
                <button onClick={() => void setzeStatus(angebot, 'versendet')} disabled={!e.online}>
                  Als versendet
                </button>
              )}
              {(angebot.status === 'versendet' || angebot.status === 'entwurf') && (
                <>
                  <button
                    onClick={() => void setzeStatus(angebot, 'angenommen')}
                    disabled={!e.online}
                  >
                    Angenommen
                  </button>
                  <button
                    onClick={() => void setzeStatus(angebot, 'abgelehnt')}
                    disabled={!e.online}
                  >
                    Abgelehnt
                  </button>
                </>
              )}
              <button
                className="schlicht"
                onClick={() => void ladeBuchhaltung(angebot)}
                disabled={!e.online}
              >
                Buchhaltungsexport
              </button>
            </div>
          </div>
        ))
      )}
    </div>
  );
}
